import { useRef, useState } from 'react';

import { useAtom } from 'jotai';
import { has } from 'lodash';
import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';
import { PromiseService } from '@/utils/services/PromiseService';

import { ErrorCustom, get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

import {
    DM_CONVERSATION_PARTICIPANT_PROFILE_PICTURE,
    DM_CONVERSATIONS
} from '@/lib/routes/apiEndpoints';

import { conversationParticipantProfilePicturesAtom } from '@/components/pages/conversations/SocialInbox/atoms';

type DMConversationsResponse = {
    data: DMConversation[];
    cursor: string;
};

type UseDMConversationsProps = {
    activeSPID: SocialProfileType['id'];
    limit?: number;
    options?: SWRInfiniteConfiguration<DMConversationsResponse, ErrorCustom>;
    cancelToken?: AbortController;
    params?: {
        name?: string;
    };
    shouldFetch?: boolean;
};

const getKey = (
    pageIndex: number,
    previousPageData: DMConversationsResponse,
    activeSPID: UseDMConversationsProps['activeSPID'],
    limit: UseDMConversationsProps['limit']
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    const url = `${injectEndpointUrlParams(DM_CONVERSATIONS, {
        SPID: activeSPID
    })}?per_page=${limit}`;

    if (pageIndex === 0) return url;

    // Next pages, include the cursor
    if (previousPageData?.cursor) {
        return `${url}&cursor=${previousPageData.cursor}`;
    }

    // No more pages
    return null;
};

export const useDMConversations = ({
    activeSPID,
    cancelToken,
    limit = 20,
    options = {},
    params = {},
    shouldFetch = true
}: UseDMConversationsProps) => {
    const [isFetching, setIsFetching] = useState(false);
    const [conversationParticipantProfilePictures, setConversationParticipantProfilePictures] =
        useAtom(conversationParticipantProfilePicturesAtom);

    const lastFetchedSPID = useRef<SocialProfileType['id'] | null>(null);

    const triggerFetchJob = async () => {
        setIsFetching(true);
        await DirectMessageService.initiateConversationFetchJob(activeSPID);
        setIsFetching(false);
    };

    const getConversationParticipantProfilePicture = async (participantId: string) => {
        const options = { signal: cancelToken?.signal };
        const url = injectEndpointUrlParams(DM_CONVERSATION_PARTICIPANT_PROFILE_PICTURE, {
            SPID: activeSPID,
            PARTICIPANT_ID: participantId
        });

        const promise = get<{ data: { profile_picture: string } }>(url, params, options);
        const response = await PromiseService.handle(promise);

        if ('rejected' in response) {
            return null;
        }

        return response.data;
    };

    const fetchProfilePictures = async (conversations: DMConversation[]) => {
        if (!conversations || !Array.isArray(conversations)) return;

        const conversationToFetch = conversations.filter(
            (conversation) =>
                !has(
                    conversationParticipantProfilePictures,
                    conversation.initiator.meta_participant_id
                )
        );

        const promises = conversationToFetch.map((conversation) =>
            getConversationParticipantProfilePicture(conversation.initiator.meta_participant_id)
        );

        const responses = await Promise.all(promises);

        setConversationParticipantProfilePictures((prev) => {
            const updated = { ...prev };
            conversationToFetch.forEach((conversation, index) => {
                const participantId = conversation.initiator.meta_participant_id;
                updated[participantId] = responses[index]?.profile_picture;
            });
            return updated;
        });
    };

    const fetchConversations = async (url: string) => {
        const options = { signal: cancelToken?.signal };
        const { data } = await get<{ data: DMConversationsResponse }>(url, params, options);
        return data;
    };

    const { data, isLoading, size, mutate, setSize, error, isValidating } = useSWRInfinite<
        DMConversationsResponse,
        ErrorCustom
    >(
        shouldFetch
            ? (pageIndex, previousPageData) =>
                  getKey(pageIndex, previousPageData, activeSPID, limit)
            : null,
        async (url) => {
            let response = await fetchConversations(url);

            /**
             * Trigger the fetch job only for the first page
             * if the data is empty
             */
            if (
                !url.includes('cursor') &&
                !response?.data?.length &&
                lastFetchedSPID.current !== activeSPID
            ) {
                lastFetchedSPID.current = activeSPID;

                triggerFetchJob();
            }

            if (response?.data) {
                fetchProfilePictures(response.data);
            }

            return response;
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateIfStale: false,
            dedupingInterval: 2000,
            ...options
        }
    );

    const loadMore = () => {
        setSize(size + 1);
    };

    const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating;

    return {
        data,
        mutate,
        error,
        loadMore,
        triggerFetchJob,
        isLoading: isFetching || isLoading,
        isLoadingMore,
        isLoadMore: data && typeof data[data.length - 1]?.cursor === 'string'
    };
};
